import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { UserType } from "@/shared/types/user.entity";

import { WalletTopUpsService } from "../services/walletTopUps.service";

// The allowlist is read at backend startup, so one answer per user and session is enough. Shared by the
// sidebar, the route guard and the wallet cards, which all ask at once.
const answers = new Map<string, boolean>();
const inflight = new Map<string, Promise<boolean>>();

function ensureAccess(userId: string): Promise<boolean> {
  const known = answers.get(userId);
  if (known !== undefined) return Promise.resolve(known);
  let promise = inflight.get(userId);
  if (!promise) {
    promise = WalletTopUpsService.access()
      .then((allowed) => {
        answers.set(userId, allowed);
        return allowed;
      })
      .finally(() => inflight.delete(userId));
    inflight.set(userId, promise);
  }
  return promise;
}

/** Whether the signed-in staff member is on `WALLET_TOP_UP_ADMIN_EMAILS`. Members and failures read false. */
export function useWalletTopUpAccess(): { allowed: boolean; isLoading: boolean } {
  const { user } = useAuth();
  const userId = user?.user_type === UserType.INTERNAL ? user.id : null;
  const [read, setRead] = useState<{ userId: string; allowed: boolean } | null>(() =>
    userId && answers.has(userId) ? { userId, allowed: answers.get(userId) === true } : null,
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    ensureAccess(userId)
      .catch(() => false)
      .then((allowed) => {
        if (!cancelled) setRead({ userId, allowed });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId) return { allowed: false, isLoading: false };
  if (read?.userId !== userId) return { allowed: false, isLoading: true };
  return { allowed: read.allowed, isLoading: false };
}
